import { Injectable } from '@angular/core';
import {
    AdvanceSearchFilter, AdvanceSearchLabels, Analysis,
    AnalysisToolTip, AnalysisType, CombineProperties, IncludeProperties,
    QuotationCommercialItem, QuotedItem
} from 'projects/dop-ordering/src/public-api';
import { OrderCommandService } from 'src/app/shared/data-service/order-command.service';
import { CompareAnalysis } from 'src/app/shared/models/order-creation/compare-analysis.model';
import { InvoiceableItems, Item } from 'src/app/shared/models/order-creation/favorite-invoicable-item.model';
import { InvoiceableItemRequest } from 'src/app/shared/models/order-creation/invoiceable-item-request.model';
import { QuotationLine } from 'src/app/shared/models/order-creation/quotation-line.model';
import { Sample } from 'src/app/shared/models/sample.model';
import { AnalysesResponse } from 'src/app/shared/models/specification/analyses-response.model';
import { AnalysesToolTipService } from '../services/analyses-tool-tip.service';
import { TranslationService } from 'projects/dop-core/src/public-api';
import { AdvanceSearchProperties } from 'src/app/shared/models/advance-search/advance-search-properties.model';
import { AdvanceSearchRequest } from 'src/app/shared/models/advance-search/advance-search-request.model';
import { OrderDataService } from 'src/app/shared/data-service/order-data.service';
import { AdvanceSearchResponse } from 'projects/dop-ordering/src/lib/models/advance-search/advance-search-response.model';
import { InvoiceableItemUpdateRequest } from 'src/app/shared/models/order-creation/invoiceable-item-update-request.model';
import { PatchInvoiceableItemRequest } from 'src/app/shared/models/order-creation/patch-invoiceable-item-request.model';
import { flatMap } from 'lodash';
import { CommercialItem } from 'src/app/shared/models/commercial-item.model';
import { select, Store } from '@ngrx/store';
import { State } from '../../store';
import { getPreparationTestData, getSalesCatalogueMrtData } from '../sales-catalogue/sales-catalogue-state.selector';
import { take } from 'rxjs/operators';
import { SalesCatalogueService } from '../sales-catalogue/sales-catalogue.service';
import { MonographDisplay } from 'src/app/shared/models/monograph/monograph-display.model';
import { MemberType, SalesCatalogueItem } from 'src/app/shared/models/sales-catalogue.model';


@Injectable({
    providedIn: 'root'
})
export class AddAnalysisService {

    constructor(
        private analysesToolTipService: AnalysesToolTipService,
        private orderCommandService: OrderCommandService,
        private orderDataService: OrderDataService,
        private store: Store<State>,
        private salesCatalogueService: SalesCatalogueService) {
    }

    //#region Analysis display
    getSelectedTests(selectedAnalysis: Analysis[], accountCode: string): any[] {
        const selectedTests: any[] = [];
        if (selectedAnalysis.length >= 2) {
            for (const analysis of selectedAnalysis) {
                if (analysis.testCode !== null) {
                    selectedTests.push({
                        accCode: accountCode,
                        qlCode: analysis.eurodatKey,
                        sgCode: analysis.subGroupKey
                    });
                    if (selectedTests.length === 4) {
                        break;
                    }
                }
            }
        }
        return selectedTests;
    }

    getPreparationTestFromSC(prepTestCodes: string[], scCode: string): SalesCatalogueItem[] {
        const scItems: SalesCatalogueItem[] = [];
        this.store.select(getPreparationTestData, { testCodes: prepTestCodes, scCode })
            .pipe(take(1)).subscribe((data: SalesCatalogueItem[]) => {
                prepTestCodes.forEach(pt => {
                    data.forEach(t => {
                        if (t.type === MemberType.TestBundle) {
                            const validTest = t.tests?.find(c => c.code === pt);
                            if (validTest) {
                                scItems.push(t);
                            }
                        } else if (t.code === pt) {
                            scItems.push(t);
                        }
                    });
                });
            });
        return scItems;
    }

    setupAnalysis(analysis: QuotedItem, favAnalysis: InvoiceableItems): QuotedItem {
        if (!favAnalysis) {
            return analysis;
        }
        this.resetFavAnalysis(analysis);
        if (favAnalysis.testQuotationLines) {
            favAnalysis.testQuotationLines?.forEach(test => {
                if (test.subGroupCode) {
                    const subGroup = analysis.quotationCommercialItem?.commercialItemSubGroups
                        .filter(y => y.comlimsCode === test.subGroupCode || y.code === test.subGroupCode).shift();
                    if (subGroup) {
                        const item = subGroup.testQuotationLines.filter(w => test.testCode ? w.testCode === test.testCode : w.code === test.code).shift();
                        if (item) {
                            item.isFavorite = !!item;
                            subGroup.isPartialFavorite = true;
                        }
                    }
                } else {
                    const item = analysis.quotationCommercialItem?.freeQuotationLinesTest?.filter(q => test.testCode ? q.testCode === test.testCode : q.code === test.code).shift();
                    if (item) { item.isFavorite = !!item; }
                }
            });
        }
        if (favAnalysis.packageQuotationLines) {
            favAnalysis.packageQuotationLines?.forEach(pck => {
                if (pck.subGroupCode) {
                    const subGroup = analysis.quotationCommercialItem?.commercialItemSubGroups
                        .filter(y => y.comlimsCode === pck.subGroupCode || y.code === pck.subGroupCode).shift();
                    if (subGroup) {
                        const item = subGroup.packageQuotationLines.filter(w => pck.packageCode? w.packageCode === pck.packageCode : w.code === pck.code).shift();
                        if (item) {
                            item.isFavorite = !!item;
                            subGroup.isPartialFavorite = true;
                        }
                    }
                } else {
                    const item = analysis.quotationCommercialItem?.freeQuotationLinesPackage?.filter(q => pck.packageCode? q.packageCode === pck.packageCode : q.code === pck.code).shift();
                    if (item) { item.isFavorite = !!item; }
                }
            });
        }
        if (favAnalysis.customerPackages) {
            favAnalysis.customerPackages.forEach(x => {
                const item = analysis.quotationCommercialItem?.customerPackages.filter(q => q.comlimsCode === x.code || q.code === x.code).shift();
                if (item) { item.isFavorite = !!item; }
            });
        }
        if (favAnalysis.subGroups) {
            favAnalysis.subGroups.forEach(x => {
                const item = analysis.quotationCommercialItem?.commercialItemSubGroups.filter(q => q.comlimsCode === x || q.code === x).shift();
                if (item) {
                    item.isFavorite = true;
                    item.packageQuotationLines.map(p => p.isFavorite = true);
                    item.testQuotationLines.map(t => t.isFavorite = true);
                }
            });
        }
        return analysis;
    }

    resetFavAnalysis(analysis : QuotedItem):QuotedItem{
        if(analysis?.quotationCommercialItem){
            if(analysis.quotationCommercialItem.commercialItemSubGroups){
                //commercialItemSubGroups reset
                    analysis.quotationCommercialItem.commercialItemSubGroups.forEach(x=> {
                        if(x.isFavorite){
                            x.isFavorite = false;
                        }
                        if(x.testQuotationLines){
                            x.testQuotationLines.forEach(element => {
                                if(element.isFavorite){
                                    element.isFavorite = false;
                                }
                                if(x.isPartialFavorite){
                                    x.isPartialFavorite = false;
                                }
                            });
                        }
                        if(x.packageQuotationLines){
                            x.packageQuotationLines.forEach(element => {
                                if(element.isFavorite){
                                    element.isFavorite = false;
                                }
                                if(x.isPartialFavorite){
                                    x.isPartialFavorite = false;
                                }
                            });
                        }
                    })
                }
        
                //freeQuotationLinesTest reset
                if(analysis.quotationCommercialItem.freeQuotationLinesTest){
                    analysis.quotationCommercialItem.freeQuotationLinesTest.forEach(x=> {
                        if(x.isFavorite){
                            x.isFavorite = false;
                        }
                    });
                }
        
                 //freeQuotationLinesPackage reset
                 if(analysis.quotationCommercialItem.freeQuotationLinesPackage){
                    analysis.quotationCommercialItem.freeQuotationLinesPackage.forEach(x=> {
                        if(x.isFavorite){
                            x.isFavorite = false;
                        }
                    });
                 }
        
                //customerPackages reset
                if(analysis.quotationCommercialItem.customerPackages){
                    analysis.quotationCommercialItem.customerPackages.forEach(x=> {
                        if(x.isFavorite){
                            x.isFavorite = false;
                        }
                    });
                }
        }

        return analysis;
    };
    //#endregion
    
    //#region Buttons
    addAnalysisToSample(sampleId: string, invoiceableItems: InvoiceableItemRequest): Promise<any> {
        return new Promise((resolve) => {
            this.orderCommandService.addAnalysisToSample(sampleId, invoiceableItems).subscribe(data => {
                resolve(data);
            });
        });
    }


    PatchAnalysisToSample(sampleId: string, invoiceableItems: InvoiceableItemRequest): Promise<any> {
        return new Promise((resolve) => {
            this.orderCommandService.patchAnalysisToSample(sampleId, invoiceableItems).subscribe(data => {
                resolve(data);
            });
        });
    }

    updateAnalysisCommentToSample(sampleId: string, comment: string, invoiceableItems: InvoiceableItemRequest): Promise<any> {
        const req = new InvoiceableItemUpdateRequest();
        req.invoiceableItem = invoiceableItems;
        req.patchRequests = [];
        comment = comment.toLowerCase() === 'none' ? null : comment;
        req.patchRequests.push(new PatchInvoiceableItemRequest('replace', 'comment', comment));
        return new Promise((resolve) => {
            this.orderCommandService.patchInvoiceableItemForSample(sampleId, req).subscribe(data => {
                resolve(data);
            });
        });
    }

    updateAnalysisIsMRTFlagToSample(sampleId: string, isMRT: string, invoiceableItems: InvoiceableItemRequest): Promise<any> {
        const req = new InvoiceableItemUpdateRequest();
        req.invoiceableItem = invoiceableItems;
        req.patchRequests = [];
        req.patchRequests.push(new PatchInvoiceableItemRequest('replace', 'ismrt', isMRT));
        return new Promise((resolve) => {
            this.orderCommandService.patchInvoiceableItemForSample(sampleId, req).subscribe(data => {
                resolve(data);
            });
        });
    }


    updateAnalysisExecutionNumberToSample(sampleId: string, executionNumber: string, invoiceableItems: InvoiceableItemRequest): Promise<any> {
        const req = new InvoiceableItemUpdateRequest();
        req.invoiceableItem = invoiceableItems;
        req.patchRequests = [];
        req.patchRequests.push(new PatchInvoiceableItemRequest('replace', 'executionNumber', executionNumber));
        return new Promise((resolve) => {
            this.orderCommandService.patchInvoiceableItemForSample(sampleId, req).subscribe(data => {
                resolve(data);
            });
        });
    }

    addAnalysisToMultipleSample(samples: Sample[], selectedAnalysis: Analysis[], quotationCode: string): Promise<any> {
        const requestModel = this.prepareSampleInvoiceableItemRequest(samples, selectedAnalysis, quotationCode);
        return new Promise((resolve) => {
            this.orderCommandService.bulkAddInvoiceableItem(requestModel).subscribe(data => {
                this.addAnalysisToViewModel(samples, selectedAnalysis, data);
                resolve(data);
            });
        });
    }

    addSCAnalysisToMultipleSample(samples: Sample[], selectedAnalysis: Analysis[]): Promise<any> {
        const selectedSCAnalysis = this.salesCatalogueService.mapAnalysisArrayToCommercialItemArray(selectedAnalysis);
        const requestModel = this.prepareSampleInvoiceableItemRequestSalesCatalogue(samples, selectedSCAnalysis);
        return new Promise((resolve) => {
            this.orderCommandService.bulkAddInvoiceableItem(requestModel).subscribe(data => {
                this.addSCAnalysisToViewModel(samples, selectedAnalysis, data);
                resolve(data);
            });
        });
    }

    mergeAnalysis(selectedAnalysis: Analysis[], alreadySelectedAnalysis: Analysis[], uncommonAnalysis?: Analysis[]): Analysis[] {

        let mergedArray = [...selectedAnalysis, ...alreadySelectedAnalysis];

        // mergedArray have duplicates, lets remove the duplicates using Set
        mergedArray = mergedArray.filter(ma => !uncommonAnalysis?.some(uca => uca.eurodatKey === ma.eurodatKey));

        const set = new Set();
        const unionArray = mergedArray.filter(item => {
            let key: string;
            if (item.analysisType === AnalysisType.MonographTest || item.analysisType === AnalysisType.MonographPackage) {
                key = item.monographCode + '|' + item.compendiumCode + '|'
                    + (item.analysisType === AnalysisType.MonographTest
                        ? item.testCode
                        : item.packageCode) + '|' + item.analysisType.toString();
            } else if (item.analysisType === AnalysisType.SalesCatalogueTest || item.analysisType === AnalysisType.SalesCataloguePackage
                || item.analysisType === AnalysisType.SalesCatalogueTestBundle) {
                key = item.salesCatalogCode + '|'
                    + (item.analysisType === AnalysisType.SalesCataloguePackage ? item.packageCode : item.testCode)
                    + '|' + item.analysisType;
            } else {

                key = item.eurodatKey;
            }

            if (!set.has(key)) {
                set.add(key);
                return true;
            }
            return false;
        }, set);

        return unionArray;
    }

    mergeCommercialItems(selectedScItems: CommercialItem[], alreadySelectedSalesCatItems: CommercialItem[], isMatrixEdited: boolean) {
        if (alreadySelectedSalesCatItems?.length > 0 && isMatrixEdited) {
            return selectedScItems;
        }

        const uniqueItems = new Map<string, CommercialItem>();
        [...selectedScItems || [], ...alreadySelectedSalesCatItems || []].forEach(item => {
            uniqueItems.set(item.code, item);
        });
     
        return Array.from(uniqueItems.values());
    }

    mergeAnalysisWithSalesCatItems(selectAnalysis: Analysis[], selectedSalesCatItems: CommercialItem[]): Analysis[] {
        for (const items of selectedSalesCatItems) {
            const analysis: Analysis = {
                testCode: items.type === AnalysisType.SalesCataloguePackage ? null : items.code,
                packageCode: items.type === AnalysisType.SalesCataloguePackage ? items.code : null,
                displayName: items.name,
                analysisType: items.type,
                price: items.price,
                salesCatalogCode: items.invoiceableItemParentCode,
                testBundleCode: items.testBundleCode,
                testBundleDisplayName: items.testBundleDisplayName,
                preparationTestCodes: items.preparationTests,
                isMandatory: items.isMandatory,
                structuredComment: items.comment,
                executionNumber: items.executionNumber,
            };
            selectAnalysis.push(analysis);
        }
        return selectAnalysis;
    }

    getUncommonAnalysis(newAnalysis: QuotedItem, alreadySelectedAnalysis: Analysis[], sampleCode: string) {
        const selectedTests = alreadySelectedAnalysis.filter(sa => sa.testCode != null && !sa.salesCatalogCode);
        const selectedPackages = alreadySelectedAnalysis.filter(sa => sa.packageCode != null && !sa.salesCatalogCode);
        const selectedCustomerPackages = alreadySelectedAnalysis.filter(sa => sa.subgroupCode != null
            && sa.isCustomerPackage && !sa.salesCatalogCode);

        const allTests = flatMap(newAnalysis?.quotationCommercialItem.commercialItemSubGroups.map(sg => sg?.testQuotationLines))
            .concat(newAnalysis?.quotationCommercialItem.freeQuotationLinesTest);
        const allPackages = flatMap(newAnalysis?.quotationCommercialItem.commercialItemSubGroups.map(sg => sg?.packageQuotationLines))
            .concat(newAnalysis?.quotationCommercialItem.freeQuotationLinesPackage);

        const uncommonSelectedTest = selectedTests
            .filter((selectedTest: any) => !allTests
                .some((t: any) => t?.testCode === selectedTest?.testCode));
        const uncommonSelectedPackage = selectedPackages
            .filter((selectedPackage: any) => !allPackages
                .some((p: any) => p?.packageCode === selectedPackage?.packageCode));
        const uncommonSelectedCustomerPackage = selectedCustomerPackages
            .filter((selectedCustomerPackage: any) => !newAnalysis?.quotationCommercialItem.customerPackages
                .some(cp => cp?.comlimsCode === selectedCustomerPackage?.subgroupCode));

        const uncommonAnalysis =
            uncommonSelectedTest.concat(uncommonSelectedPackage, uncommonSelectedCustomerPackage);
        uncommonAnalysis.map(x => x.sampleCode = sampleCode);

        return uncommonAnalysis;
    }

    prepareSampleInvoiceableItemRequest(samples: Sample[], selectedAnalysis: Analysis[], quotationCode: string, updateComment?: boolean)
        : InvoiceableItemRequest {
        const requestModel = new InvoiceableItemRequest();
        selectedAnalysis.forEach(x => {
            let addToSamples = [];
            if (updateComment) {
                addToSamples = samples;
            } else {
                if (x.analysisType === AnalysisType.MonographTest || x.analysisType === AnalysisType.MonographPackage) {
                    addToSamples = samples.filter(s => s.analyses == null || s.analyses.map(y => y.testCode).indexOf(x.testCode) === -1
                        || s.analyses.map(y => y.packageCode).indexOf(x.packageCode) === -1);
                }
                else if (x.analysisType === AnalysisType.SalesCatalogueTest || x.analysisType === AnalysisType.SalesCataloguePackage) {
                        addToSamples = samples.filter(s => s.analyses == null || s.analyses.map(y => y.testCode).indexOf(x.testCode) === -1
                            || s.analyses.map(y => y.packageCode).indexOf(x.packageCode) === -1);
                }
                else {
                    addToSamples = samples.filter(s => s.analyses == null
                        || s.analyses.map(a => a.eurodatKey).indexOf(x?.eurodatKey) === -1);
                }
            }
            const sampleCodes = addToSamples.map(s => s.sampleCode);
            if (sampleCodes && sampleCodes.length > 0) {
                const quotationLine = new QuotationLine();
                quotationLine.sequence = x.sequence;
                quotationLine.invoiceableItemParentCode = quotationCode;
                quotationLine.sampleCodes = sampleCodes;
                quotationLine.executionNumber = x.executionNumber;
                switch (x.analysisType) {
                    case AnalysisType.FreeQLTest:
                        quotationLine.code = x.eurodatKey;
                        quotationLine.isMandatory = x.isMandatory ?? false;
                        requestModel.testQuotationLine = requestModel.testQuotationLine ?? [];
                        requestModel.testQuotationLine.push(quotationLine);
                        break;
                    case AnalysisType.FreeQLPackage:
                        quotationLine.code = x.eurodatKey;
                        requestModel.packageQuotationLine = requestModel.packageQuotationLine ?? [];
                        requestModel.packageQuotationLine.push(quotationLine);
                        break;
                    case AnalysisType.CustomerPackage:
                        quotationLine.code = x.subGroupKey;
                        requestModel.customerPackage = requestModel.customerPackage ?? [];
                        requestModel.customerPackage.push(quotationLine);
                        break;
                    case AnalysisType.SubgroupWithTest:
                        quotationLine.code = x.eurodatKey;
                        quotationLine.isMandatory = x.isMandatory ?? false;
                        quotationLine.subGroupCode = x.subGroupKey;
                        requestModel.testQuotationLine = requestModel.testQuotationLine ?? [];
                        requestModel.testQuotationLine.push(quotationLine);
                        break;
                    case AnalysisType.SubgroupWithPackage:
                        quotationLine.code = x.eurodatKey;
                        quotationLine.subGroupCode = x.subGroupKey;
                        requestModel.packageQuotationLine = requestModel.packageQuotationLine ?? [];
                        requestModel.packageQuotationLine.push(quotationLine);
                        break;
                    case AnalysisType.MonographTest:
                        requestModel.test = requestModel.test ?? [];
                        const item: CommercialItem = {
                            code: x.testCode,
                            invoiceableItemParentCode: x.salesCatalogCode,
                            monographCode: x.monographCode,
                            compendiumCode: x.compendiumCode,
                            sequence: x.sequence,
                            isMandatory: x.isMandatory,
                            executionNumber: x.executionNumber,
                        };
                        if (samples.length > 0) {
                            item.sampleCodes = item.sampleCodes ?? [];
                            item.sampleCodes = samples.map(y => y.sampleCode);
                        }
                        requestModel.test.push(item);
                        break;
                    case AnalysisType.SalesCataloguePackage:
                    case AnalysisType.MonographPackage:
                        const scPackage: CommercialItem = {
                            code: x.packageCode,
                            invoiceableItemParentCode: x.salesCatalogCode,
                            sequence: x.sequence,
                            executionNumber: x.executionNumber,
                        };

                        if (x.analysisType === AnalysisType.MonographPackage) {
                            scPackage.monographCode = x.monographCode;
                            scPackage.compendiumCode = x.compendiumCode;
                        }

                        if (samples.length > 0) {
                            scPackage.sampleCodes = scPackage.sampleCodes ?? [];
                            scPackage.sampleCodes = samples.map(y => y.sampleCode);
                        }
                        requestModel.package = requestModel.package ?? [];
                        requestModel.package.push(scPackage);
                        break;
                    case AnalysisType.SalesCatalogueTest:
                    case AnalysisType.SalesCatalogueTestBundle:
                        requestModel.test = requestModel.test ?? [];
                        const scTest: CommercialItem = {
                            code: x.testCode,
                            invoiceableItemParentCode: x.salesCatalogCode,
                            testBundleCode: x.testBundleCode,
                            sequence: x.sequence,
                            isMandatory: x.isMandatory ?? false,
                            executionNumber: x.executionNumber,
                        };
                        if (samples.length > 0) {
                            scTest.sampleCodes = scTest.sampleCodes ?? [];
                            scTest.sampleCodes = samples.map(y => y.sampleCode);
                        }
                        requestModel.test.push(scTest);
                        break;
                    default:
                        break;
                }
            }
        });
        return requestModel;
    }

    prepareSampleInvoiceableItemRequestForPatch(samples: Sample[], patchItem: Analysis, quotationCode: string)
        : InvoiceableItemRequest {
        const requestModel = new InvoiceableItemRequest();
        const sampleCodes = samples.map(s => s.sampleCode);
        if (sampleCodes && sampleCodes.length > 0) {
            const quotationLine = new QuotationLine();
            quotationLine.sequence = patchItem.sequence;
            quotationLine.invoiceableItemParentCode = quotationCode;
            quotationLine.sampleCodes = sampleCodes;
            quotationLine.executionNumber = patchItem.executionNumber;
            switch (patchItem.analysisType) {
                case AnalysisType.FreeQLTest:
                    quotationLine.code = patchItem.eurodatKey;
                    quotationLine.isMandatory = patchItem.isMandatory ?? false;
                    requestModel.testQuotationLine = requestModel.testQuotationLine ?? [];
                    requestModel.testQuotationLine.push(quotationLine);
                    break;
                case AnalysisType.FreeQLPackage:
                    quotationLine.code = patchItem.eurodatKey;
                    requestModel.packageQuotationLine = requestModel.packageQuotationLine ?? [];
                    requestModel.packageQuotationLine.push(quotationLine);
                    break;
                case AnalysisType.CustomerPackage:
                    quotationLine.code = patchItem.subGroupKey;
                    requestModel.customerPackage = requestModel.customerPackage ?? [];
                    requestModel.customerPackage.push(quotationLine);
                    break;
                case AnalysisType.SubgroupWithTest:
                    quotationLine.code = patchItem.eurodatKey;
                    quotationLine.isMandatory = patchItem.isMandatory ?? false;
                    quotationLine.subGroupCode = patchItem.subGroupKey;
                    requestModel.testQuotationLine = requestModel.testQuotationLine ?? [];
                    requestModel.testQuotationLine.push(quotationLine);
                    break;
                case AnalysisType.SubgroupWithPackage:
                    quotationLine.code = patchItem.eurodatKey;
                    quotationLine.subGroupCode = patchItem.subGroupKey;
                    requestModel.packageQuotationLine = requestModel.packageQuotationLine ?? [];
                    requestModel.packageQuotationLine.push(quotationLine);
                    break;
                case AnalysisType.MonographTest:
                    requestModel.test = requestModel.test ?? [];
                    const item: CommercialItem = {
                        code: patchItem.testCode,
                        invoiceableItemParentCode: patchItem.salesCatalogCode,
                        monographCode: patchItem.monographCode,
                        compendiumCode: patchItem.compendiumCode,
                        sequence: patchItem.sequence,
                        isMandatory: patchItem.isMandatory,
                    };
                    if (samples.length > 0) {
                        item.sampleCodes = item.sampleCodes ?? [];
                        item.sampleCodes = samples.map(y => y.sampleCode);
                    }
                    requestModel.test.push(item);
                    break;
                case AnalysisType.SalesCataloguePackage:
                case AnalysisType.MonographPackage:
                    const scPackage: CommercialItem = {
                        code: patchItem.packageCode,
                        invoiceableItemParentCode: patchItem.salesCatalogCode,
                        sequence: patchItem.sequence,
                    };

                    if (patchItem.analysisType === AnalysisType.MonographPackage) {
                        scPackage.monographCode = patchItem.monographCode;
                        scPackage.compendiumCode = patchItem.compendiumCode;
                    }

                    if (samples.length > 0) {
                        scPackage.sampleCodes = scPackage.sampleCodes ?? [];
                        scPackage.sampleCodes = samples.map(y => y.sampleCode);
                    }
                    requestModel.package = requestModel.package ?? [];
                    requestModel.package.push(scPackage);
                    break;
                case AnalysisType.SalesCatalogueTest:
                case AnalysisType.SalesCatalogueTestBundle:
                    requestModel.test = requestModel.test ?? [];
                    const scTest: CommercialItem = {
                        code: patchItem.testCode,
                        invoiceableItemParentCode: patchItem.salesCatalogCode,
                        testBundleCode: patchItem.testBundleCode,
                        sequence: patchItem.sequence,
                        isMandatory: patchItem.isMandatory ?? false
                    };
                    if (samples.length > 0) {
                        scTest.sampleCodes = scTest.sampleCodes ?? [];
                        scTest.sampleCodes = samples.map(y => y.sampleCode);
                    }
                    requestModel.test.push(scTest);
                    break;
                default:
                    break;
            }
        }
        return requestModel;
    }

    PrepareSampleSalesCatalogRequestForPatch(samples: Sample[], patchItem: CommercialItem) {
        const requestModel = new InvoiceableItemRequest();
        const sampleCodes = samples.map(s => s.sampleCode);
        if (sampleCodes && sampleCodes.length > 0) {
            switch (patchItem.type) {
                case AnalysisType.SalesCatalogueTest:
                    requestModel.test = requestModel.test ?? [];
                    const item: CommercialItem = {
                        sequence: patchItem.sequence,
                        code: patchItem.code,
                        name: patchItem.name,
                        comment: patchItem.comment,
                        invoiceableItemParentCode: patchItem.invoiceableItemParentCode,
                        testBundleCode: patchItem.testBundleCode,
                        isMandatory: patchItem.isMandatory ?? false,
                        executionNumber: patchItem.executionNumber,
                    };
                    if (samples.length > 0) {
                        item.sampleCodes = item.sampleCodes ?? [];
                        item.sampleCodes = samples.map(y => y.sampleCode);
                    }
                    requestModel.test.push(item);
                    break;
                case AnalysisType.SalesCataloguePackage:
                    const scPackage: CommercialItem = {
                        code: patchItem.code,
                        sequence: patchItem.sequence,
                        name: patchItem.name,
                        comment: patchItem.comment,
                        invoiceableItemParentCode: patchItem.invoiceableItemParentCode,
                        executionNumber: patchItem.executionNumber,
                    };
                    if (samples.length > 0) {
                        scPackage.sampleCodes = scPackage.sampleCodes ?? [];
                        scPackage.sampleCodes = samples.map(y => y.sampleCode);
                    }
                    requestModel.package = requestModel.package ?? [];
                    requestModel.package.push(scPackage);
                    break;
                case AnalysisType.SalesCatalogueTestBundle:
                    requestModel.test = requestModel.test ?? [];
                    const membertests: any[] = [];
                    for (const membertest of membertests) {
                        const testBundleitem: CommercialItem = {
                            code: membertest.code,
                            sequence: patchItem.sequence,
                            name: membertest.name,
                            comment: patchItem.comment,
                            invoiceableItemParentCode: patchItem.invoiceableItemParentCode,
                            testBundleCode: patchItem.testBundleCode,
                            executionNumber: patchItem.executionNumber,
                        };
                        if (samples.length > 0) {
                            testBundleitem.sampleCodes = item.sampleCodes ?? [];
                            testBundleitem.sampleCodes = samples.map(y => y.sampleCode);
                        }
                        requestModel.test.push(testBundleitem);
                    }
                    break;
            }
        }
        return requestModel;
    }

    prepareSampleInvoiceableItemRequestSalesCatalogue(samples: Sample[], selectedAnalysis: CommercialItem[]): InvoiceableItemRequest {
        const requestModel = new InvoiceableItemRequest();
        selectedAnalysis.forEach(x => {
            const addToSamples = samples.filter(s => s.analyses == null || !s.analyses.find(a => a.testCode === x.code
                && a.salesCatalogCode === x.invoiceableItemParentCode && a.testBundleCode === x.testBundleCode));
            const sampleCodes = addToSamples.map(s => s.sampleCode);
            if (sampleCodes && sampleCodes.length > 0) {
                switch (x.type) {
                    case AnalysisType.SalesCatalogueTest:
                        requestModel.test = requestModel.test ?? [];
                        const item: CommercialItem = {
                            sequence: x.sequence,
                            code: x.code,
                            name: x.name,
                            comment: x.comment,
                            invoiceableItemParentCode: x.invoiceableItemParentCode,
                            testBundleCode: x.testBundleCode,
                            isMandatory: x.isMandatory ?? false,
                            executionNumber: x.executionNumber,
                        };
                        if (samples.length > 0) {
                            item.sampleCodes = item.sampleCodes ?? [];
                            item.sampleCodes = samples.map(y => y.sampleCode);
                        }
                        requestModel.test.push(item);
                        break;
                    case AnalysisType.SalesCataloguePackage:
                        const scPackage: CommercialItem = {
                            code: x.code,
                            sequence: x.sequence,
                            name: x.name,
                            comment: x.comment,
                            invoiceableItemParentCode: x.invoiceableItemParentCode,
                            executionNumber: x.executionNumber,
                        };
                        if (samples.length > 0) {
                            scPackage.sampleCodes = scPackage.sampleCodes ?? [];
                            scPackage.sampleCodes = samples.map(y => y.sampleCode);
                        }
                        requestModel.package = requestModel.package ?? [];
                        requestModel.package.push(scPackage);
                        break;
                    case AnalysisType.SalesCatalogueTestBundle:
                        requestModel.test = requestModel.test ?? [];
                        const membertests: any[] = [];
                        for (const membertest of membertests) {
                            const testBundleitem: CommercialItem = {
                                code: membertest.code,
                                sequence: x.sequence,
                                name: membertest.name,
                                comment: x.comment,
                                invoiceableItemParentCode: x.invoiceableItemParentCode,
                                testBundleCode: x.testBundleCode,
                                executionNumber: x.executionNumber,
                            };
                            if (samples.length > 0) {
                                testBundleitem.sampleCodes = item.sampleCodes ?? [];
                                testBundleitem.sampleCodes = samples.map(y => y.sampleCode);
                            }
                            requestModel.test.push(testBundleitem);
                        }
                        break;
                }
            }
        });
        return requestModel;
    }

    sortCommercialItems(analysis: Analysis[]) {
        return analysis.sort((a, b) => {
            let result: number;
            // if sort index of two ci is same
            if (a.sortIndex === b.sortIndex) {
                const aType = a.analysisType;
                const bType = b.analysisType;
                // for same sort index of (SG / CP) & (T/P): consider (T/P) first
                // for same sort index of CP & SG : consider QuotationSubGroupCode
                // If (T/P) have the same sorting number, then sort by alphanumerical order of the CI code
                if (aType === AnalysisType.SubgroupWithTest &&
                    (bType === AnalysisType.FreeQLTest || bType === AnalysisType.FreeQLPackage)) {
                    result = 1;
                } else if (bType === AnalysisType.SubgroupWithTest && (aType === AnalysisType.FreeQLTest || AnalysisType.FreeQLPackage)) {
                    result = -1;
                } else if ((aType === AnalysisType.SubgroupWithTest && bType === AnalysisType.SubgroupWithTest) ||
                    (aType === AnalysisType.FreeQLTest || aType === AnalysisType.FreeQLPackage)
                    && (bType === AnalysisType.FreeQLTest || bType === AnalysisType.FreeQLPackage)) {
                    result = a.eurodatKey > b.eurodatKey ? 1 : -1;
                }
            } else {
                result = a.sortIndex - b.sortIndex;
            }
            return result;
        });
    }

    getUniquePreparationTests(prepTestAnalysis: Analysis[], alreadySelectedAnalysis: Analysis[], scAnalysis: CommercialItem[]) {
        const set = new Set();
        prepTestAnalysis = prepTestAnalysis.filter(c => !alreadySelectedAnalysis?.map(a => a.testCode).includes(c.testCode) &&
            !scAnalysis?.map(a => a.code).includes(c.testCode));
        const unionArray = prepTestAnalysis.filter(item => {
            if (!set.has(item.testCode)) {
                set.add(item.testCode);
                return true;
            }
            return false;
        }, set);
        return unionArray;
    }

    private addAnalysisToViewModel(samples: Sample[], selectedAnalysis: Analysis[], analysesResponse: AnalysesResponse[] = []) {
        samples.forEach(x => {
            if (analysesResponse instanceof Array) {
                const planningLines = analysesResponse.filter(resp => String(resp.referenceId) === x.sampleCode);
                if (planningLines && planningLines.length > 0) {
                    x.planningLines = x.planningLines ? [...x.planningLines.concat(planningLines)] : planningLines;
                    x.analyses = x.analyses ?
                        [...x.analyses.concat(selectedAnalysis.filter(s => x.analyses.map(a => a.eurodatKey).indexOf(s.eurodatKey) === -1
                            || !(x.analyses.some(b => (s.testCode ?? null) === (b.testCode ?? null)
                                && (s.packageCode ?? null) === (b.packageCode ?? null)
                                && (s.monographCode ?? null) === (b.monographCode ?? null))))).map(s => {
                                    const sequenceFromAnalysesResponse = analysesResponse.find(resp => 
                                        resp.referenceId === x.sampleCode && resp.testCode === s.testCode
                                    )?.id;
        
                                    return {
                                        ...s,
                                        sequence: sequenceFromAnalysesResponse ?? s.sequence
                                    };
                                })
                            ] :
                        [...selectedAnalysis.map(s => {
                            const sequenceFromAnalysesResponse = analysesResponse.find(resp => 
                                resp.referenceId === x.sampleCode && resp.testCode === s.testCode
                            )?.id;
    
                            return {
                                ...s,
                                sequence: sequenceFromAnalysesResponse ?? s.sequence
                            };
                        })];
                }
            }
        });
    }

    private addSCAnalysisToViewModel(samples: Sample[], selectedAnalysis: Analysis[], analysesResponse: AnalysesResponse[] = []) {
        samples.forEach(x => {
            const planningLines = analysesResponse.filter(resp => String(resp.referenceId) === x.sampleCode);
            x.planningLines = x.planningLines ? [...x.planningLines.concat(planningLines)] : planningLines;
            x.analyses = x.analyses ?
                [...x.analyses.concat(selectedAnalysis.filter(s =>
                    !x.analyses.find(a =>
                        a.testCode === s.testCode
                        && a.salesCatalogCode === s.salesCatalogCode
                        && a.testBundleCode === s.testBundleCode)))] :
                [...selectedAnalysis];
        });
    }

    //To-Do: Not being used anywhere in the project -- so commented
    // private addMonographAnalysisToViewModel(samples: Sample[], selectedAnalysis: Analysis[], analysesResponse: AnalysesResponse[] = []) {
    //     samples.forEach(x => {
    //         const planningLines = analysesResponse.filter(resp => String(resp.referenceId) === x.sampleCode);
    //         x.planningLines = x.planningLines ? [...x.planningLines.concat(planningLines)] : planningLines;
    //         x.analyses = x.analyses ?
    //             [...x.analyses.concat(selectedAnalysis.filter(s => x.analyses.map(a => a.testCode
    //                 && a.salesCatalogCode && a.monographCode && a.compendiumCode)
    //                 .indexOf(s.salesCatalogCode && s.testCode) === -1))] :
    //             [...selectedAnalysis];
    //     });
    // }
    //#endregion

    //#region Compare
    async getCompareData(selectedTests: any[], analysis: QuotationCommercialItem): Promise<AnalysisToolTip[]> {
        const compareData: AnalysisToolTip[] = [];
        const maxLength = selectedTests.length;
        let loop = 0;
        for (const test of selectedTests) {

            const subGroup = test.sgCode != null
                ? analysis.commercialItemSubGroups.filter(c => c.code === test.sgCode).shift()
                : null;
            this.analysesToolTipService.getTestToolTipData(test.accCode,
                test.qlCode).subscribe(data => {
                    if (subGroup !== null) {
                        data.testName = subGroup.name + ' - ' + data.testName;
                    }
                    data.parameters.subscribe((x: any) => {
                        data.parameters = x;
                        compareData.push(data);
                        loop++;
                    });
                });
        }
        await this.waitFor((_: any) => loop === maxLength);
        return compareData;
    }

    compareAnalysisMapper(uncheckedComparedAnalysis: CompareAnalysis[]): Analysis[] {
        const analysisList: Analysis[] = [];
        uncheckedComparedAnalysis.forEach(x => {
            const analysis = new Analysis();
            analysis.eurodatKey = x.code;
            analysisList.push(analysis);
        });
        return analysisList;
    }

    // Method used to wait until a condition is met
    private waitFor(conditionFunction: any) {

        const poll = (resolve: any) => {
            if (conditionFunction()) {
                resolve();
            } else {
                setTimeout(_ => poll(resolve), 200);
            }
        };

        return new Promise(poll);
    }
    //#endregion

    //#region Advance Search

    getAdvanceSearchLabels(): AdvanceSearchLabels {
        const advanceSearchLabels = new AdvanceSearchLabels();
        advanceSearchLabels.advancedSearchLabel = TranslationService.translate('vbeiAdvancedSearch');
        advanceSearchLabels.testInstructionsLabel = TranslationService.translate('vbeiTestInstructions');
        advanceSearchLabels.descriptionLabel = TranslationService.translate('vbeiDescription');
        advanceSearchLabels.appliedOnLabel = TranslationService.translate('vbeiAppliedOn');
        advanceSearchLabels.objectiveLabel = TranslationService.translate('vbeiObjective');
        advanceSearchLabels.parameterCodeLabel = TranslationService.translate('vbeiParameterCode');
        advanceSearchLabels.parameterNameLabel = TranslationService.translate('vbeiParameterName');
        advanceSearchLabels.methodLabel = TranslationService.translate('vbeiMethodAOAC');
        advanceSearchLabels.CASNumberLabel = TranslationService.translate('vbeiCASNumber');
        advanceSearchLabels.alsoSearchOnLabel = TranslationService.translate('vbeiAlsoSearchOn');
        advanceSearchLabels.refineSearchLabel = TranslationService.translate('vbeiRefineSearch');
        advanceSearchLabels.clearAllLabel = TranslationService.translate('vbeiClearAll');
        advanceSearchLabels.combineSearchOnLabel = TranslationService.translate('vbeiCombineSearchOn');

        return advanceSearchLabels;
    }

    getAdvanceSearchResult(quotationCode: string, req: AdvanceSearchRequest): Promise<AdvanceSearchResponse[]> {
        return new Promise((resolve) => {
            this.orderDataService.getAdvanceSearchResult(quotationCode, req).subscribe(data => {
                return resolve(data);
            });
        });
    }

    createRequestObject(filter: AdvanceSearchFilter): AdvanceSearchRequest {
        const advanceSearchRequest = new AdvanceSearchRequest();
        advanceSearchRequest.searchText = filter.searchText;
        advanceSearchRequest.combineProperties = this.getCombineProperties(filter.combineProperties);
        advanceSearchRequest.includeProperties = this.getIncludeProperties(filter.includeProperties, filter.combineProperties);
        return advanceSearchRequest;
    }

    getIncludeProperties(prop: IncludeProperties, combine: CombineProperties): AdvanceSearchProperties[] {
        const include: AdvanceSearchProperties[] = [];
        if (prop.comments) {
            include.push(AdvanceSearchProperties.Comments);
        }
        if (prop.description) {
            include.push(AdvanceSearchProperties.Description);
        }
        if (prop.method) {
            include.push(AdvanceSearchProperties.Method);
        }
        if (prop.objective) {
            include.push(AdvanceSearchProperties.Objective);
        }
        if (prop.applicableOn) {
            include.push(AdvanceSearchProperties.ApplicableOn);
        }
        if (prop.parameterCode && !combine.parameterCode) {
            include.push(AdvanceSearchProperties.ParameterCode);
        }
        if (prop.parameterName && !combine.parameterName) {
            include.push(AdvanceSearchProperties.ParameterName);
        }
        if (prop.CASNumber && !combine.CASNumber) {
            include.push(AdvanceSearchProperties.CASNumber);
        }
        return include;
    }

    getCombineProperties(prop: CombineProperties): {} {
        const dict = {};
        if (!!prop.comments) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['Comments'] = prop.comments;
        }
        if (!!prop.description) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['Description'] = prop.description;
        }
        if (!!prop.method) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['Method'] = prop.method;
        }
        if (!!prop.objective) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['Objective'] = prop.objective;
        }
        if (!!prop.applicableOn) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['ApplicableOn'] = prop.applicableOn;
        }
        if (!!prop.parameterCode) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['ParameterCode'] = prop.parameterCode;
        }
        if (!!prop.parameterName) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['ParameterName'] = prop.parameterName;
        }
        if (!!prop.CASNumber) {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            dict['CASNumber'] = prop.CASNumber;
        }
        return dict;

    }

    filterQuotation(quotedItems: QuotedItem, advanceSearchResponse: AdvanceSearchResponse[]): QuotedItem {
        const commercialItem = quotedItems.quotationCommercialItem;

        // Filter Free Quotation Tests
        const freeTests = advanceSearchResponse.filter(f => !f.subGroupCode && !f.packageCode).map(m => m.testCode);
        commercialItem.freeQuotationLinesTest.forEach(test => {
            test.hideItem = freeTests.indexOf(test.testCode) === -1;
        });

        // Filter Free Quotation Packages
        const freePackages = advanceSearchResponse.filter(f => !f.subGroupCode).map(m => m.packageCode);
        commercialItem.freeQuotationLinesPackage.forEach(pack => {
            pack.hideItem = freePackages.indexOf(pack.packageCode) === -1;
        });

        // Filter Customer Packages
        const customerPackage = advanceSearchResponse.filter(m => m.isCustomerPackage)?.map(m => m.subGroupCode);
        commercialItem.customerPackages.forEach(cp => {
            cp.hideItem = customerPackage.indexOf(cp.comlimsCode) === -1;
        });

        // Filter Subgroups and internal items of the subgroup
        commercialItem.commercialItemSubGroups.forEach(subGroup => {
            const subGroupItems = advanceSearchResponse.filter(f => !f.isCustomerPackage
                && f.subGroupCode === subGroup.comlimsCode);

            const tCodes = subGroupItems?.map(m => m.testCode);
            subGroup.testQuotationLines.forEach(test => {
                test.hideItem = tCodes.indexOf(test.testCode) === -1;
            });

            const packCodes = subGroupItems?.map(m => m.packageCode);
            subGroup.packageQuotationLines.forEach(packageItem => {
                packageItem.hideItem = packCodes.indexOf(packageItem.packageCode) === -1;
            });

            subGroup.hideItem = subGroup.testQuotationLines.every(test => test.hideItem)
                && subGroup.packageQuotationLines.every(pac => pac.hideItem);
        });

        return quotedItems;
    }

    //#endregion Advance Search

    //#region SalesCatalogue

    getAllPreparationTestsForSalesCatalogue(selectedAnalysis: CommercialItem[], sample?: Sample, quotedItems?: QuotedItem): CommercialItem[] {
        const uniquePreparationTests: CommercialItem[] = [];
        selectedAnalysis?.forEach(analysis => {
            if (analysis.preparationTests) {
                this.store.pipe(select(getSalesCatalogueMrtData, { analysis }))
                    .pipe(take(1)).subscribe(data => {
                        (data as any[])?.forEach(mrtItem => {

                            // check if mrt already present in quotation
                            let isExistInSG: boolean = false;
                            const isExistInTQ = quotedItems.quotationCommercialItem.freeQuotationLinesTest
                                .some(x => x.testCode == mrtItem.code);

                            quotedItems.quotationCommercialItem.commercialItemSubGroups
                                .forEach(x => {
                                    isExistInSG = x.testQuotationLines.some(x => x.testCode == mrtItem.code);
                                });

                            if (!isExistInSG && !isExistInTQ) {
                                const mrt: CommercialItem = {
                                    code: mrtItem.code,
                                    name: mrtItem.name,
                                    comment: analysis.comment,
                                    type: AnalysisType.SalesCatalogueTest,
                                    price: mrtItem.price,
                                    invoiceableItemParentCode: analysis.invoiceableItemParentCode,
                                    executionNumber: analysis.executionNumber,
                                    isMandatory: true
                                };

                                // Only consider mrt if
                                // 1. Its not previously selected
                                // 2. Not manually selected now
                                // 3. If multiple Main tests share same prepTests add only once
                                if (!(sample.analyses?.some(x => x.testCode === mrt.code) ||
                                    selectedAnalysis?.some(x => x.code === mrt.code) ||
                                    uniquePreparationTests.some(x => x.code === mrt.code))) {
                                    uniquePreparationTests.push(mrt);
                                }
                            }
                        });
                    });
            }
        });

        return uniquePreparationTests;
    }

    //#region SalesCatalogue

    //#region Monograph
    mapMonographDisplayToAnalysis(monographItems: MonographDisplay[], salesCatalogCode: string): Analysis[] {
        const selectAnalysis: Analysis[] = [];
        for (const item of monographItems) {
            const analysis: Analysis = {
                testCode: item.type === AnalysisType.MonographTest ? item.analysisCode : null,
                packageCode: item.type === AnalysisType.MonographPackage ? item.analysisCode : null,
                displayName: item.analysisName,
                analysisType: item.type,
                price: item.price ? item.priceValue : null,
                salesCatalogCode,
                monographCode: item.compoundCode,
                compendiumCode: item.compendiumCode,
                monographName: item.compound,
                compendiumName: item.compendium,
                preparationTestCodes: item.preparationTests?.map(c => c.analysisCode),
                isMandatory: item.isMandatory,
                executionNumber: item.executionNumber,
            };
            selectAnalysis.push(analysis);
        }
        return selectAnalysis;

    }

    mergeMonographDisplay(selectedMonographs: MonographDisplay[], alreadySelectedMonographs: MonographDisplay[]) {
        const mergedArray = [...selectedMonographs, ...alreadySelectedMonographs];
        const set = new Set();
        const unionArray = mergedArray.filter(item => {
            if (!set.has(item.analysisCode)) {
                set.add(item.analysisCode);
                return true;
            }
            return false;
        }, set);
        return unionArray;
    }
    //#endregion Monograph

}
